// Ekran 01 — Stok Kartları.
// Sınıf adları ref_files/btys-erp-01-stok-karti.html'den birebir alındı.

import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Kabuk from '../../components/Kabuk'
import { useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { useFitInsp } from '../../lib/useFitInsp'
import { sayiyaCevirBos, tr, trGiris, trTarih } from '../../lib/trFormat'
import {
  stokKartlari,
  type DegerCift,
  type StokKartiOzet,
  type StokTuru,
  type VeriGuveni,
} from './api'
import {
  HizliCariDrawer,
  ImportDrawer,
  KopyaDrawer,
  LiteraturDrawer,
} from './StokKartiDrawerlari'
import TamSayfaKart, { GRUPLAR, TUR_ADI } from './TamSayfaKart'
import { useSekmeler } from '../../components/Sekmeler'
import { useYetki, YetkiKapisi } from '../../lib/yetki'
import Sayfalayici from '../../components/Sayfalayici'
import IceAktarDrawer from '../../components/IceAktarDrawer'

const SEGMENTLER: { anahtar: StokTuru | 'tumu'; etiket: string }[] = [
  { anahtar: 'tumu', etiket: 'Tümü' },
  { anahtar: 'hammadde', etiket: 'Hammadde' },
  { anahtar: 'urun', etiket: 'Ürün' },
  { anahtar: 'sarf', etiket: 'Sarf' },
  { anahtar: 'yedek', etiket: 'Yedek Parça' },
  { anahtar: 'yakit', etiket: 'Yakıt' },
]

/** K32 veri güveni rozetleri. */
const GUVEN: Record<VeriGuveni, { simge: string; metin: string; sinif: string }> = {
  olcum: { simge: '●', metin: 'ölçüm', sinif: 'guv-o' },
  kurum: { simge: '◐', metin: 'kurumsal', sinif: 'guv-k' },
  celisik: { simge: '⚠', metin: 'çelişik', sinif: 'guv-c' },
}

function GuvenRozeti({ guven }: { guven: VeriGuveni | null }) {
  if (!guven) return null
  const g = GUVEN[guven]
  return <span className={`guv ${g.sinif}`}>{g.simge} {g.metin}</span>
}

/**
 * Referans `seviye()` (btys-erp-01:412): hammadde ve kritik seviyesi olmayan
 * kartta rozet YOK; diğerlerinde mevcut stok kritik seviyeyle karşılaştırılır.
 *
 * Stok defterden okunuyor (§5.1'de kartın bakiye sütunu yok). Defterde hiç izi
 * olmayan kartta seviye BİLİNMİYOR ve yine rozet basılmıyor — orada "Yeterli"
 * ya da "Tükendi" yazmak uydurma olurdu (K32).
 */
function seviye(kart: StokKartiOzet): { sinif: string; metin: string } | null {
  if (kart.tur === 'hammadde' || kart.kritikSeviye == null) return null
  if (kart.mevcutStok == null) return null
  if (kart.mevcutStok <= 0) return { sinif: 'b-red', metin: 'Tükendi' }
  if (kart.mevcutStok < kart.kritikSeviye) return { sinif: 'b-amber', metin: 'Kritik altı' }
  return { sinif: 'b-gray', metin: 'Yeterli' }
}

function SeviyeRozeti({ kart }: { kart: StokKartiOzet }) {
  const s = seviye(kart)
  if (!s) return <span style={{ color: '#8A98AB' }}>—</span>
  return <span className={`badge ${s.sinif}`}>{s.metin}</span>
}

/**
 * Referans `durumB()` (btys-erp-01:413) — panel başlığındaki aktif/pasif hapı.
 * Tablodaki `.pasif` soluğu satır seçilince kaybolduğu için paneldeki tek
 * pasiflik işareti budur.
 */
function DurumRozeti({ durum }: { durum: 'aktif' | 'pasif' }) {
  return durum === 'aktif' ? (
    <span className="badge b-navy"><span className="dot" />Aktif</span>
  ) : (
    <span className="badge b-gray"><span className="dot" />Pasif</span>
  )
}

/**
 * Tablodaki "Mevcut" kolonu. Referans tek bir `r.mev` tutuyor; bizde hammaddenin
 * stoğu firma×ürün havuzlarında (K26), havuzsuz türlerin stoğu defterde durur.
 */
function mevcutMiktar(kart: StokKartiOzet): number | null {
  return kart.havuzBakiye ?? kart.mevcutStok
}

/** Etkin değer ipucunun metni — referans `F()` kaynağı böyle yazıyor (K13). */
const KAYNAK_METNI: Record<string, string> = {
  analiz: 'Analiz değeri',
  tesis: 'Tesis değeri (literatürü ezer)',
  lit: 'Literatür değeri',
}

/**
 * Panel alanı → `tesis_deger.alan` anahtarı (§5.1 kolon adları).
 * Panelde görünen ETKİN değer düzenlenince yazı bu ara katmana düşer (K13).
 */
const TESIS_ALANI: Record<string, string> = {
  km: 'km',
  okm: 'okm_km',
  verim: 'verim_nl_kg_okm',
  ch4: 'ch4_oran',
}

const SAYFA_BOYU = 25

function Icerik() {
  // Referans curTur='tumu' ile açılıyor.
  const [tur, setTur] = useState<StokTuru | 'tumu'>('tumu')
  const [arama, setArama] = useState('')
  const [grup, setGrup] = useState('')
  const [hijOnly, setHijOnly] = useState(false)
  const [sayfa, setSayfa] = useState(1)
  const [seciliKod, setSeciliKod] = useState<string | null>(null)
  const panel = useFitInsp<HTMLDivElement>()
  const { ac } = useDrawer()
  const sekmeler = useSekmeler()
  const kuyruk = useQueryClient()
  const { izinVar } = useYetki()
  const yazabilir = izinVar('StokKartlari', 'yaz')

  function tamSayfaAc(kod: string, ad: string) {
    sekmeler.ac({ id: `c-${kod}`, baslik: ad, icerik: <TamSayfaKart kod={kod} /> })
  }

  const { data: sayaclar = [] } = useQuery({
    queryKey: ['kart-sayaclari'],
    queryFn: stokKartlari.sayaclar,
  })

  const { data: ozet } = useQuery({
    queryKey: ['kart-ozeti'],
    queryFn: stokKartlari.ozet,
  })

  const { data: hamListe = [], isLoading } = useQuery({
    queryKey: ['kartlar', tur, arama],
    queryFn: () => stokKartlari.liste(tur === 'tumu' ? undefined : tur, arama || undefined),
  })

  // Grup ve hijyen filtreleri istemcide — referans `filtered()` de böyle yapıyor
  // ve liste tek tesisin kartlarıyla sınırlı, sunucuya gitmeye değmez.
  const kartlar = hamListe.filter(
    (k) => (!grup || k.grup === grup) && (!hijOnly || k.hij),
  )

  const sayfaSayisi = Math.max(1, Math.ceil(kartlar.length / SAYFA_BOYU))
  const gecerliSayfa = Math.min(sayfa, sayfaSayisi)
  const sayfadakiler = kartlar.slice((gecerliSayfa - 1) * SAYFA_BOYU, gecerliSayfa * SAYFA_BOYU)

  // Referans açılışta bir satırı seçili getiriyor (selectRow("SK-0009")) —
  // sağ panel boş bir kutu olarak durmasın.
  const ilkKod = sayfadakiler[0]?.kod
  useEffect(() => {
    if (!seciliKod && ilkKod) setSeciliKod(ilkKod)
  }, [seciliKod, ilkKod])

  const { data: secili } = useQuery({
    queryKey: ['kart', seciliKod],
    queryFn: () => stokKartlari.detay(seciliKod!),
    enabled: !!seciliKod,
  })

  const { data: teorik } = useQuery({
    queryKey: ['teorik', seciliKod],
    queryFn: () => stokKartlari.teorik(seciliKod!),
    enabled: !!seciliKod && secili?.tur === 'hammadde',
  })

  // Panelin "Son Hareketler" listesi. Hammadde dalında referans bunun yerine
  // "Stok & Son Analizler" basıyor, o yüzden yalnız diğer türlerde çekiliyor.
  const { data: sonHareketler = [] } = useQuery({
    queryKey: ['kart-hareketleri', seciliKod],
    queryFn: () => stokKartlari.hareketler(seciliKod!),
    enabled: !!seciliKod && !!secili && secili.tur !== 'hammadde',
  })

  // Hammadde dalının üçüncü bölümü "Stok & Son Analizler" (ref:496): havuz
  // bakiyesi + son iki lot analizi. Yalnız hammaddede çekiliyor.
  const { data: sonAnalizler = [] } = useQuery({
    queryKey: ['kart-analizleri', seciliKod],
    queryFn: () => stokKartlari.analizler(seciliKod!),
    enabled: !!seciliKod && !!secili && secili.tur === 'hammadde',
  })

  /**
   * Panelin düzenlenebilir alanlarının taslağı. Kaydet düğmesi `.insp-f`'te,
   * alanlar `.insp-b`'de olduğu için durum ortak atada duruyor.
   *
   * Alan taslakta YOKSA sunucudaki değer gösterilir; böylece odak dönüşündeki
   * yeniden çekme kullanıcının yazdığını silmiyor. Taslak yalnız seçim
   * değişince ve başarılı kayıttan sonra sıfırlanır.
   */
  const [taslak, setTaslak] = useState<Record<string, string>>({})
  useEffect(() => setTaslak({}), [seciliKod])

  /**
   * Panel alanlarının SUNUCUDAKİ karşılığı. Kaydet yalnız bunlardan sapan
   * alanları gönderiyor; hiç sapma yoksa düğme kapalı kalıyor — boş bir PUT
   * atmak "kaydedildi" toast'ını yalancı yapardı.
   */
  const temel: Record<string, string> =
    !secili
      ? {}
      : secili.tur === 'hammadde'
        ? {
            km: trGiris(teorik?.km.deger),
            okm: trGiris(teorik?.okmYuzde.deger),
            verim: trGiris(teorik?.verim.deger),
            ch4: trGiris(teorik?.ch4.deger),
          }
        : { kritik: trGiris(secili.kritikSeviye), birim: secili.birim ?? '' }

  const degisenler = Object.keys(temel).filter(
    (ad) => taslak[ad] !== undefined && taslak[ad] !== temel[ad],
  )

  // Panelin "Stok durumu" ilk kolonu rozetle AYNI eşikten renkleniyor
  // (referans: sv.t==='Tükendi' ? 'red' : 'Kritik altı' ? 'amber' : '').
  const stokSeviyesi = secili ? seviye(secili) : null
  const stokRengi =
    stokSeviyesi?.sinif === 'b-red' ? 'red' : stokSeviyesi?.sinif === 'b-amber' ? 'amber' : ''

  const alanDegeri = (ad: string) => taslak[ad] ?? temel[ad] ?? ''
  const alanYaz = (ad: string, deger: string) => setTaslak((t) => ({ ...t, [ad]: deger }))

  const kaydet = useMutation({
    mutationFn: async () => {
      if (!secili) throw new Error('Kart seçili değil.')

      if (secili.tur === 'hammadde') {
        // K13: panelde ETKİN değer görünüyor; yazılan değer tesis katmanına
        // düşer ve literatürü ezer. Alanı boşaltmak tesis değerini siler.
        const degerler: Record<string, number | null> = {}
        for (const ad of degisenler) degerler[TESIS_ALANI[ad]] = sayiyaCevirBos(alanDegeri(ad))
        await stokKartlari.tesisDegerleri(secili.id, degerler)
        return `${secili.kod} · ${degisenler.length} etkin değer tesis katmanına yazıldı (literatürü ezer)`
      }

      // Sunucu PUT'ta 27 alanın tamamını bekliyor; mevcut kartın üzerine yalnız
      // panelde düzenlenenler yazılıyor.
      const kritik = sayiyaCevirBos(alanDegeri('kritik'))
      const yeniKart = await stokKartlari.kaydet(secili.id, {
        ...secili,
        birim: alanDegeri('birim').trim(),
        kritikSeviye: kritik,
      })
      return `${yeniKart.kod} kaydedildi — kritik seviye ${tr(kritik, 0)} ${yeniKart.birim}`
    },
    onSuccess: async (mesaj) => {
      setTaslak({})
      await Promise.all([
        kuyruk.invalidateQueries({ queryKey: ['kart', seciliKod] }),
        kuyruk.invalidateQueries({ queryKey: ['teorik', seciliKod] }),
        kuyruk.invalidateQueries({ queryKey: ['kartlar'] }),
        kuyruk.invalidateQueries({ queryKey: ['kart-ozeti'] }),
      ])
      toast(mesaj)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Stok kartı kaydedilemedi.'),
  })

  /**
   * Referans `F()`: etkin değer kutusu + kaynağını söyleyen ipucu (K13).
   *
   * Bileşen değil düz bir üretici: iç bileşen tanımlansaydı her tuş vuruşunda
   * yeni tip üretilir, girdi yeniden bağlanır ve odak kaybolurdu. Yazma yetkisi
   * yoksa alan salt-okunur kalıyor — değerin kendisi okunabilir bilgi.
   */
  function etkinAlanKutusu(ad: string, etiket: string, cift: DegerCift) {
    return (
      <div className="fld" key={ad}>
        <label>{etiket}</label>
        <input
          className="in mono"
          placeholder="—"
          value={alanDegeri(ad)}
          readOnly={!yazabilir}
          title={
            yazabilir
              ? 'Yazılan değer TESİS değeri olur ve literatürü ezer (K13); boş bırakmak literatüre döndürür'
              : 'Bu modülde yazma yetkiniz yok'
          }
          onChange={(e) => alanYaz(ad, e.target.value)}
        />
        <div className={`fld-hint ${cift.kaynak === 'tesis' ? 'tesis' : ''}`}>
          {cift.kaynak ? KAYNAK_METNI[cift.kaynak] : 'Değer girilmemiş'}
        </div>
      </div>
    )
  }

  const sayac = useMemo(() => {
    const h = new Map(sayaclar.map((s) => [s.tur as string, s.adet]))
    const toplam = sayaclar.reduce((a, s) => a + s.adet, 0)
    return (a: StokTuru | 'tumu') => (a === 'tumu' ? toplam : (h.get(a) ?? 0))
  }, [sayaclar])

  // Yerleşim btys-erp-01-stok-karti.html'den birebir:
  //   section.pane.on > .crumb + .page-h + .kstrip + .list-grid(.card + aside.insp.card)
  // section.pane sarmalayıcısını SekmePanolari basıyor.
  return (
    <>
      <div className="crumb">
        Ana Veri <b>/ Stok Kartları</b>
      </div>

      <div className="page-h">
        <h1 className="page-t">Stok Kartları</h1>
        <div className="page-actions">
          <button
            className="btn btn-ghost"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Excel’den içe aktar',
                altBaslik: 'Şablon: btys-literatur-kutuphanesi formatı',
                boyut: 'sm',
                icerik: <ImportDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            Excel’den içe aktar
          </button>
          <button
            className="btn btn-sec"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Literatürden kart türet',
                altBaslik: 'Global kütüphane · KTBL / VDI 4630',
                boyut: 'md',
                icerik: <LiteraturDrawer />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            Literatürden türet
          </button>
          <button
            className="btn btn-sec"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? 'Excel şablonuyla toplu yükle' : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              ac({
                baslik: 'Stok kartlarını içe aktar',
                altBaslik: 'Excel şablonuyla toplu yükleme',
                icerik: (
                  <IceAktarDrawer
                    baslik="stok kartı"
                    sablonYolu="/api/stok-kartlari/sablon"
                    sablonAdi="btys-stok-karti-sablonu.xlsx"
                    iceAktarYolu="/api/stok-kartlari/ice-aktar"
                    tazeleAnahtari={['kartlar', 'kart-sayaclari', 'kart-ozeti']}
                  />
                ),
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
            </svg>
            İçe aktar
          </button>
          <button
            className="btn btn-pri"
            type="button"
            disabled={!yazabilir}
            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
            onClick={() =>
              sekmeler.ac({
                id: 'yeni',
                baslik: 'Yeni Stok Kartı',
                icerik: <TamSayfaKart kod={null} yeniTur={tur === 'tumu' ? 'hammadde' : tur} />,
              })
            }
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Yeni stok kartı
          </button>
        </div>
      </div>

      <div className="kstrip">
        <div className="k">
          <div className="k-l">Toplam Kart</div>
          <div className="k-v">{ozet?.toplamKart ?? '—'}</div>
          <div className="k-s">5 tür · tüm gruplar</div>
        </div>
        <div className="k">
          <div className="k-l">Hammadde</div>
          <div className="k-v">{ozet?.aktifHammadde ?? '—'}</div>
          <div className="k-s">aktif substrat kartı</div>
        </div>
        <div className="k">
          <div className="k-l">Kritik Seviye Altı</div>
          <div className="k-v amber">{ozet?.kritikAlti ?? '—'}</div>
          <div className="k-s">ikmal gerekiyor</div>
        </div>
        <div className="k">
          <div className="k-l">Stoğu Tükenen</div>
          <div className="k-v red">{ozet?.stoguTukenen ?? '—'}</div>
          <div className="k-s">acil tedarik</div>
        </div>
        <div className="k">
          <div className="k-l">Hijyenizasyon Gerekli</div>
          <div className="k-v">{ozet?.hijyenGerekli ?? '—'}</div>
          <div className="k-s">mezbaha kaynaklı (K19)</div>
        </div>
      </div>

      <div className="list-grid">
        <div className="card">
          <div className="toolbar">
            <div className="seg">
              {SEGMENTLER.map((s) => (
                <button
                  key={s.anahtar}
                  type="button"
                  className={tur === s.anahtar ? 'seg-i on' : 'seg-i'}
                  onClick={() => {
                    setTur(s.anahtar)
                    setGrup('')
                    setSayfa(1)
                    // Referans: hammadde/tümü dışına çıkınca hijyen filtresi düşer.
                    if (s.anahtar !== 'tumu' && s.anahtar !== 'hammadde') setHijOnly(false)
                    setSeciliKod(null)
                  }}
                >
                  {s.etiket}
                  <small>{sayac(s.anahtar)}</small>
                </button>
              ))}
            </div>
            <div className="f-search">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                placeholder="Kod veya ad ara…"
                value={arama}
                onChange={(e) => {
                  setArama(e.target.value)
                  setSayfa(1)
                }}
              />
            </div>
            <select
              className="sel"
              value={grup}
              onChange={(e) => {
                setGrup(e.target.value)
                setSayfa(1)
              }}
            >
              <option value="">{tur === 'hammadde' ? 'Tüm kategoriler' : 'Tüm gruplar'}</option>
              {(tur === 'tumu' ? [] : GRUPLAR[tur]).map((g) => (
                <option key={g}>{g}</option>
              ))}
            </select>
            {/* Hijyen çipi yalnız hammadde ve tümü segmentlerinde görünür. */}
            {(tur === 'tumu' || tur === 'hammadde') && (
              <button
                className={hijOnly ? 'chip on' : 'chip'}
                type="button"
                onClick={() => {
                  setHijOnly((h) => !h)
                  setSayfa(1)
                }}
              >
                Hijyenizasyon gerekli
              </button>
            )}
            <div className="toolbar-right">
              <span>{kartlar.length} kayıt</span>
            </div>
          </div>

          {/* Referansta tablo doğrudan .card içinde; yatay kaydırma
              body{overflow-x:auto} ile çözülüyor, ek sarmalayıcı yok. */}
          <table className="grid">
            <thead>
                {tur === 'hammadde' ? (
                  <tr>
                    <th style={{ width: 78 }}>Kod</th>
                    <th>Substrat</th>
                    <th style={{ width: 170 }}>Kategori</th>
                    <th style={{ width: 96 }}>Hijyen</th>
                    <th className="num" style={{ width: 58 }}>KM %</th>
                    <th className="num" style={{ width: 76 }}>OKM %KM</th>
                    <th className="num" style={{ width: 104 }}>Verim Nm³/t</th>
                    <th className="num" style={{ width: 56 }}>CH₄ %</th>
                    <th className="num" style={{ width: 76 }}>Havuz t</th>
                    <th style={{ width: 64 }} />
                  </tr>
                ) : tur === 'tumu' ? (
                  <tr>
                    <th style={{ width: 78 }}>Kod</th>
                    <th>Ad</th>
                    <th style={{ width: 190 }}>Grup / Kategori</th>
                    <th style={{ width: 56 }}>Birim</th>
                    <th className="num" style={{ width: 84 }}>Mevcut</th>
                    <th style={{ width: 92 }}>Seviye</th>
                    <th style={{ width: 64 }} />
                  </tr>
                ) : (
                  <tr>
                    <th style={{ width: 78 }}>Kod</th>
                    <th>Ad</th>
                    <th style={{ width: 180 }}>Grup</th>
                    <th style={{ width: 56 }}>Birim</th>
                    <th className="num" style={{ width: 96 }}>Kritik</th>
                    <th className="num" style={{ width: 84 }}>Mevcut</th>
                    <th style={{ width: 96 }}>Seviye</th>
                    <th style={{ width: 64 }} />
                  </tr>
                )}
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Yükleniyor…</td></tr>
                )}
                {!isLoading && kartlar.length === 0 && (
                  <tr><td colSpan={9} className="cell-sub" style={{ padding: 14 }}>Kayıt yok.</td></tr>
                )}
                {sayfadakiler.map((k: StokKartiOzet) => (
                  <tr
                    key={k.id}
                    className={[
                      seciliKod === k.kod ? 'sel' : '',
                      k.durum === 'pasif' ? 'pasif' : '',
                    ].filter(Boolean).join(' ')}
                    title={k.durum === 'pasif' ? 'Pasif kayıt' : undefined}
                    onClick={() => setSeciliKod(k.kod)}
                    onDoubleClick={() => tamSayfaAc(k.kod, k.ad)}
                  >
                    <td><span className="kod">{k.kod}</span></td>
                    <td>
                      <div className="cell-name">
                        {k.ad} <GuvenRozeti guven={k.veriGuven} />
                        {k.inhibisyonRiski && (
                          <span className="warn-i" title={k.inhibisyonRiski}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01" />
                            </svg>
                          </span>
                        )}
                      </div>
                      {k.inhibisyonRiski && <div className="cell-sub">{k.inhibisyonRiski}</div>}
                    </td>
                    <td>{k.grup ?? '—'}</td>
                    {tur === 'hammadde' ? (
                      <>
                        <td>
                          {k.hij
                            ? <span className="badge b-amber"><span className="dot" />Gerekli</span>
                            : <span style={{ color: '#8A98AB' }}>—</span>}
                        </td>
                        <td className="num">{tr(k.km, 1)}</td>
                        <td className="num">{tr(k.okmKm, 1)}</td>
                        <td className="num">{tr(k.verimNlKgOkm, 0)}</td>
                        <td className="num">{tr(k.ch4Oran, 1)}</td>
                        <td className="num">{tr(k.havuzBakiye, 1)}</td>
                      </>
                    ) : tur === 'tumu' ? (
                      <>
                        <td className="tur-t">{k.birim}</td>
                        <td className="num">{tr(mevcutMiktar(k), 1)}</td>
                        <td><SeviyeRozeti kart={k} /></td>
                      </>
                    ) : (
                      <>
                        <td className="tur-t">{k.birim}</td>
                        <td className="num">{tr(k.kritikSeviye, 0)}</td>
                        <td className="num">{tr(mevcutMiktar(k), 1)}</td>
                        <td><SeviyeRozeti kart={k} /></td>
                      </>
                    )}
                    <td>
                      <div className="row-acts">
                        <button
                          className="mini-btn"
                          type="button"
                          disabled={!yazabilir}
                          title={yazabilir ? 'Kopyala' : 'Bu modülde yazma yetkiniz yok'}
                          onClick={(e) => {
                            e.stopPropagation()
                            ac({
                              baslik: 'Stok kartını kopyala',
                              altBaslik: `${k.kod} · ${k.ad}`,
                              boyut: 'sm',
                              icerik: <KopyaDrawer kart={k} />,
                            })
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </button>
                        <button
                          className="mini-btn"
                          type="button"
                          title="Tam sayfa aç"
                          onClick={(e) => {
                            e.stopPropagation()
                            tamSayfaAc(k.kod, k.ad)
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <div className="grid-foot">
            <span>
              Toplam {ozet?.toplamKart ?? '—'} stok kartı · sayfa başına {SAYFA_BOYU} kayıt
            </span>
            <Sayfalayici gecerli={gecerliSayfa} toplam={sayfaSayisi} git={setSayfa} />
          </div>
        </div>

        {/* ---- inceleme paneli ---- */}
        <aside className="insp card" ref={panel}>
          {!secili ? (
            <div className="insp-empty">
              Bir kayıt seçin —<br />detaylar burada anında görünür.
            </div>
          ) : (
            <>
              <div className="insp-h">
                {/* ref:475 — kod · tür · grup. "Tümü" segmentinde tablo tür
                    sütunu taşımıyor; türün okunabildiği tek yer burası. */}
                <div className="insp-eyebrow">
                  <span className="kod">{secili.kod}</span>
                  <span>·</span>
                  <span>{TUR_ADI[secili.tur]}</span>
                  <span>·</span>
                  <span>{secili.grup ?? '—'}</span>
                </div>
                {/* ref:476 — ad · Hijyen · seviye · durum (dört parça). */}
                <div className="insp-t">
                  {secili.ad}{' '}
                  {secili.hij && <span className="badge b-amber">Hijyen</span>}{' '}
                  {stokSeviyesi && (
                    <span className={`badge ${stokSeviyesi.sinif}`}>{stokSeviyesi.metin}</span>
                  )}{' '}
                  <DurumRozeti durum={secili.durum} />
                </div>
                {secili.inhibisyonRiski && (
                  <div className="cell-sub" style={{ marginTop: 4 }}>{secili.inhibisyonRiski}</div>
                )}
              </div>

              <div className="insp-b">
                {secili.tur === 'hammadde' && teorik && (
                  <>
                    <div className="insp-sec">
                      <div className="insp-sec-t">Etkin Parametreler — hızlı düzenle</div>
                      {/* Dördü de ETKİN değeri gösterir: verim/CH₄ de K13 zincirinden
                          geçiyor (tesis değeri varsa literatürü ezer), o yüzden ham
                          kart alanı değil `teorik` yanıtı okunuyor. */}
                      <div className="fgrid">
                        {etkinAlanKutusu('km', 'KM (% YM)', teorik.km)}
                        {etkinAlanKutusu('okm', 'OKM (% KM)', teorik.okmYuzde)}
                        {etkinAlanKutusu('verim', 'Verim (Nm³/t oKM)', teorik.verim)}
                        {etkinAlanKutusu('ch4', 'CH₄ (%)', teorik.ch4)}
                      </div>
                      {/* ref:488 — veri güveni rozeti referansta panel başlığında
                          değil, bu ipucunun ekinde duruyor (K32). */}
                      <div className="fld-hint" style={{ marginTop: 7 }}>
                        Öncelik: analiz → tesis → literatür (K13)
                        {secili.veriGuven && (
                          <>
                            {' · Veri güveni: '}
                            <GuvenRozeti guven={secili.veriGuven} />
                          </>
                        )}
                      </div>
                    </div>

                    {/* "1 ton için teorik" — K31 gereği metan kaynağı yazılır */}
                    <div className="insp-sec">
                      <div className="inst">
                        <div className="inst-t">1 ton için teorik</div>
                        <div className="inst-3">
                          <div className="inst-c">
                            <div className="inst-v">{tr(teorik.biyogaz, 0)}</div>
                            <div className="inst-l">Nm³ biyogaz</div>
                          </div>
                          <div className="inst-c">
                            <div className="inst-v">{tr(teorik.metan, 0)}</div>
                            <div className="inst-l">Nm³ metan</div>
                          </div>
                          <div className="inst-c">
                            <div className="inst-v">{tr(teorik.elektrik, 0)}</div>
                            <div className="inst-l">kWh elektrik</div>
                          </div>
                        </div>
                        <div className="inst-note">
                          Metan kaynağı:{' '}
                          {teorik.metanKaynagi === 'bmp'
                            ? 'ölçülen BMP'
                            : teorik.metanKaynagi === 'ch4'
                              ? 'verim × CH₄% (türetilmiş)'
                              : 'hesaplanamadı — veri eksik'}
                        </div>
                      </div>
                    </div>

                    {/* ref:496-501 — hammadde dalının üçüncü ve son bölümü. */}
                    <div className="insp-sec">
                      <div className="insp-sec-t">Stok &amp; Son Analizler</div>
                      <div className="mini-list">
                        <div className="mini-li">
                          Havuz bakiyesi
                          <span className="vv">{tr(secili.havuzBakiye, 1)} t</span>
                        </div>
                        {sonAnalizler.length === 0 && (
                          <div className="mini-li">Bu kartın lot analizi yok</div>
                        )}
                        {sonAnalizler.map((a, i) => (
                          <div className="mini-li" key={`${a.numuneNo}-${i}`}>
                            {/* .dt sütunu 40px: referans gün.ay basıyor */}
                            <span className="dt">{trTarih(a.zaman).slice(0, 5)}</span>
                            {a.numuneNo} · KM
                            <span className="vv">{tr(a.km, 1)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {secili.tur !== 'hammadde' && (
                  <>
                    <div className="insp-sec">
                      <div className="insp-sec-t">Seviye Ayarları — hızlı düzenle</div>
                      <div className="fgrid">
                        <div className="fld">
                          <label>Kritik seviye ({secili.birim})</label>
                          {/* trGiris: gidiş dönüşü kayıpsız — tr(v,2) ile yazıp
                              okumak numeric(12,3)'ün 3. ondalığını yuvarlıyordu. */}
                          <input
                            className="in mono"
                            placeholder="—"
                            value={alanDegeri('kritik')}
                            readOnly={!yazabilir}
                            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
                            onChange={(e) => alanYaz('kritik', e.target.value)}
                          />
                        </div>
                        <div className="fld">
                          <label>Birim</label>
                          <input
                            className="in"
                            value={alanDegeri('birim')}
                            readOnly={!yazabilir}
                            title={yazabilir ? undefined : 'Bu modülde yazma yetkiniz yok'}
                            onChange={(e) => alanYaz('birim', e.target.value)}
                          />
                        </div>
                        <div className="fld">
                          <label>Raf / konum</label>
                          {/* §5.1 stok_karti'nda raf/konum kolonu yok; migration
                              eklemeden yazılacak yer olmadığı için alan kapalı.
                              Referansta sabit metin ("Depo D-2 · Raf 4") vardı. */}
                          <input
                            className="in"
                            placeholder="—"
                            disabled
                            title="Kartta raf/konum alanı yok (§5.1) — depo yerleşimi henüz modellenmedi"
                          />
                        </div>
                        <div className="fld">
                          <label>Tedarikçi</label>
                          <div className="with-plus">
                            {/* Kartta varsayılan tedarikçi kolonu da yok; seçim
                                hiçbir yere yazılamayacağı için liste kapalı.
                                Yanındaki + gerçekten cari oluşturur (yan etkisi var). */}
                            <select
                              className="in js-ted"
                              disabled
                              title="Kartta varsayılan tedarikçi alanı yok (§5.1) — tedarik ilişkisi firma×ürün havuzundan okunur (K26)"
                            >
                              <option>—</option>
                            </select>
                            <button
                              className="plus-btn"
                              type="button"
                              disabled={!yazabilir}
                              title={
                                yazabilir
                                  ? 'İşlemi bozmadan yeni cari ekle'
                                  : 'Bu modülde yazma yetkiniz yok'
                              }
                              onClick={() =>
                                ac({
                                  baslik: 'Yeni cari — hızlı kayıt',
                                  altBaslik: 'Mevcut işleminiz açık kalır',
                                  boyut: 'sm',
                                  icerik: <HizliCariDrawer />,
                                })
                              }
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 5v14M5 12h14" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="insp-sec">
                      <div className="inst">
                        <div className="inst-t">Stok durumu</div>
                        <div className="inst-3">
                          <div className="inst-c">
                            <div className={`inst-v ${stokRengi}`}>{tr(secili.mevcutStok, 1)}</div>
                            <div className="inst-l">mevcut {secili.birim}</div>
                          </div>
                          <div className="inst-c">
                            <div className="inst-v">{tr(secili.kritikSeviye, 0)}</div>
                            <div className="inst-l">kritik seviye</div>
                          </div>
                          <div className="inst-c">
                            <div className="inst-v">{tr(secili.aylikTuketim, 0)}</div>
                            <div className="inst-l">aylık tüketim</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="insp-sec">
                      <div className="insp-sec-t">Son Hareketler</div>
                      <div className="mini-list">
                        {sonHareketler.length === 0 && (
                          <div className="mini-li">Bu kartın defterde hareketi yok</div>
                        )}
                        {sonHareketler.map((h, i) => (
                          <div className="mini-li" key={`${h.belgeNo}-${i}`}>
                            {/* .dt sütunu 40px: referans gün.ay basıyor */}
                            <span className="dt">{trTarih(h.zaman).slice(0, 5)}</span>
                            {h.etiket}
                            <span
                              className={h.yon === 1 ? 'vv plus' : h.yon === -1 ? 'vv minus' : 'vv'}
                            >
                              {h.yon === 1 ? '+' : h.yon === -1 ? '−' : ''}
                              {tr(h.miktar, 1)} {h.birim}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {/* 27 teknik alan burada DEĞİL: referansta `derin()` içinde,
                    yani tam sayfada duruyor (ref:578/651) — panelde ikinci kez
                    basılıyordu. Karşılığı TamSayfaKart'ın `.ptab` tabloları. */}
              </div>

              {/* Referans altlığı: Tam sayfa · Kopyala · Kaydet.
                  "Literatürden türet" buradan kaldırıldı — aynı eylem sayfa
                  başlığında zaten var ve seçili kartla hiçbir ilgisi yok. */}
              <div className="insp-f">
                <button
                  className="btn btn-sec btn-sm"
                  type="button"
                  onClick={() => tamSayfaAc(secili.kod, secili.ad)}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3" />
                  </svg>
                  Tam sayfa
                </button>
                <button
                  className="btn btn-sec btn-sm"
                  type="button"
                  title="Kartı kopyala"
                  onClick={() =>
                    ac({
                      baslik: 'Stok kartını kopyala',
                      altBaslik: `${secili.kod} · ${secili.ad}`,
                      boyut: 'sm',
                      icerik: <KopyaDrawer kart={secili} />,
                    })
                  }
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Kopyala
                </button>
                <button
                  className="btn btn-pri btn-sm"
                  style={{ flex: 1, justifyContent: 'center' }}
                  type="button"
                  disabled={!yazabilir || degisenler.length === 0 || kaydet.isPending}
                  title={
                    !yazabilir
                      ? 'Bu modülde yazma yetkiniz yok'
                      : degisenler.length === 0
                        ? 'Değişiklik yok — bir alanı düzenleyince etkinleşir'
                        : undefined
                  }
                  onClick={() => kaydet.mutate()}
                >
                  {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </>
          )}
        </aside>
      </div>
    </>
  )
}

export default function StokKartlariSayfasi() {
  return (
    <Kabuk listeBasligi="Stok Kartları">
      <YetkiKapisi modul="StokKartlari">
        <Icerik />
      </YetkiKapisi>
    </Kabuk>
  )
}
